import { store } from "common/store";
import {
  currentOrgErrors,
  currentOrgId,
  currentOrgJobMenuItems,
  currentOrgLoading,
  currentOrgPrefConfig,
  currentOrgRootFolderId,
  org,
} from "common/selectors/org.selectors";
import { authenticated } from "common/selectors/auth.selectors";
import {
  currentUser,
  currentUserErrors,
  currentUserLoading,
  currentUserOrgId,
  userAclCheck,
} from "common/selectors/user.selectors";
import { RootState } from "common/reducers";
import { AclKeys, AclValues } from "common/records/User";
import { List } from "immutable";
import {
  currentJob,
  currentJobErrors,
  currentJobId,
  currentJobLoading,
  currentJobRootFolderId,
} from "common/selectors/job.selectors";
import { Nullable } from "common/types";
import { estimateDocIds, estimateDocsLoaded } from "common/selectors/estimate.selectors";
import { JobRecord, fromJSON as jobFromJSON } from "common/records/Job";
import { OrgRecord } from "common/records/Org";
import { OrgAsyncActions } from "common/reducers/org.async.actions";

export const watchRedux = async (
  selector: (state: RootState) => any,
  errorsSelector?: (state: RootState) => any,
  timeout = 20_000,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    let unsubscribe: Function | null = null;

    const checkValue = () => {
      const state = store.getState();
      const value = selector(state);

      if (value) {
        resolve(value);
        if (unsubscribe) {
          unsubscribe();
        }
      }

      if (errorsSelector && errorsSelector(state) && errorsSelector(state).size > 0) {
        reject(new Error(errorsSelector(state)));
        if (unsubscribe) {
          unsubscribe();
        }
      }
    };

    unsubscribe = store.subscribe(checkValue);

    checkValue();

    setTimeout(() => {
      if (unsubscribe) {
        unsubscribe();
      }
      reject(new Error("Loader Timeout"));
    }, timeout);
  });
};

export const waitCheckUserAcl = async (resource: AclKeys, permission: AclValues) => {
  try {
    await watchRedux((state) => !currentUserLoading(state), currentUserErrors);
    return userAclCheck(store.getState(), { resource, permission });
  } catch {
    return false;
  }
};

export const waitCheckUserOrgPrefConfig = async (path: string[]) => {
  try {
    await watchRedux((state) => !currentOrgLoading(state), currentUserErrors);
    return currentOrgPrefConfig(store.getState(), { path });
  } catch {
    return false;
  }
};

export const waitCheckJobMenu = async (menuItemId: string) => {
  try {
    await watchRedux((state) => currentOrgId(state) && !currentJobLoading(state), currentJobErrors);
    return currentOrgJobMenuItems(store.getState()).includes(menuItemId);
  } catch {
    return false;
  }
};

export const watchCurrentOrgId = async (): Promise<number> => {
  try {
    return watchRedux((state) => currentOrgId(state), currentJobErrors);
  } catch {
    return 0;
  }
};

export const watchCurrentOrgPrefConfig = async (path: (string | number)[]): Promise<Nullable<any>> => {
  try {
    return await watchRedux((state) => currentOrgId(state) && currentOrgPrefConfig(state, { path }), currentOrgErrors);
  } catch {
    return null;
  }
};

export const waitCheckCurrentOrgLoaded = async (): Promise<Nullable<OrgRecord>> => {
  try {
    const orgId = await watchCurrentOrgId();
    if (!org(store.getState(), { orgId })) {
      await store.dispatch(OrgAsyncActions.getOrg(orgId));
    }
    return watchRedux((state) => org(state, { orgId }));
  } catch {
    return null;
  }
};

export const watchCurrentUserOrgId = async (): Promise<number> => {
  try {
    return watchRedux((state) => currentUserOrgId(state), currentUserErrors);
  } catch {
    return 0;
  }
};

export const watchCurrentJob = async (): Promise<JobRecord | null> => {
  try {
    return await watchRedux((state) => {
      const job = currentJob(state);
      if (!job || job.loading) return null;

      return job;
    }, currentJobErrors);
  } catch {
    return null;
  }
};

export const watchFolderParentId = async (
  folderName: string,
  documentableType: "Org" | "Job" = "Org",
): Promise<Nullable<number>> => {
  try {
    return await watchRedux(
      (state) =>
        documentableType === "Org"
          ? currentOrgId(state) && currentOrgRootFolderId(state, { folderName })
          : currentJobId(state) && currentJobRootFolderId(state, { folderName }),
      (state) => currentOrgErrors(state) || currentJobErrors(state),
    );
  } catch {
    return null;
  }
};

export const watchCurrentUserId = async (): Promise<Nullable<number>> => {
  try {
    return await watchRedux((state) => currentUser(state)?.get("id"), currentUserErrors);
  } catch {
    return null;
  }
};

/**
 * Returns true if the user is not authenticated OR if the `authUser` param is present in the URL
 * Returning true if `authUser` param is present is to prevent loaders from running twice.
 * `protectedRedirectLoader` removes the authUser param via a redirect, triggering all loaders to run again.
 * @param request The loader's arg.request
 * @returns boolean
 */
export const notAuthenticated = (request: Request) => {
  const url = new URL(request.url);
  const uid = url.searchParams.get("authUser");
  if (uid) return true;
  return !authenticated(store.getState());
};

export const waitEnabledMeasurements = async (): Promise<List<string>> => {
  try {
    await watchRedux((state) => currentOrgId(state) && !currentJobLoading(state), currentJobErrors);
    return currentOrgPrefConfig(store.getState(), { path: ["measurement", "job_tab"], notSet: List([]) });
  } catch {
    return List();
  }
};

export const waitCurrentEstimateDocs = async (estimateId: number): Promise<List<number>> => {
  try {
    await watchRedux((state) => estimateDocsLoaded(state) === true);

    const docIds = estimateDocIds(store.getState(), { estimateId });

    return docIds || List<number>();
  } catch {
    return List<number>();
  }
};
